/* eslint camelcase: ["error", {properties: "never"}] */

import React, {Fragment, useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, Redirect} from 'react-router-dom';
import {Badge, Button, Fade, Container, Row, Col, Form, Tab, Tabs, Modal} from 'react-bootstrap';
import {Formik} from 'formik';
import {FilePond} from 'react-filepond';
import CommonMetaTag from './common-meta-tag';
import DatePicker, {registerLocale} from 'react-datepicker';
import Dialog from 'react-bootstrap-dialog';
import {useTranslation} from 'react-i18next';
import Configuration from '../config';
import 'react-datepicker/dist/react-datepicker.css';
import it from 'date-fns/locale/it';
import {changeSubscription, reportBookings, reportSubscriptions, reportTransactions, changeStorageType, showStorageModal, hideStorageModal} from '../actions/player';
import PlayerNewSubscription from './player-new-subscription';
import PlayerInsufficientFunds from './player-insufficient-funds';
import {subscriptionKind, affiliationType, imageTypes, renewalType} from '../constants';
import {showMessage} from '../actions/messages';
import AuthMethods from './auth-methods';
import {FaArchive, FaArrowUp, FaCalendarAlt, FaDatabase, FaFileExcel, FaIdCard, FaPercent, FaSave, FaSyncAlt, FaTags, FaTicketAlt, FaTimes, FaTrophy, FaUser, FaWallet} from 'react-icons/fa';
import {getPlayerSportRankings, requestPlayerSportRankingVerification, savePlayerSportRankingFederation, savePlayerSportRankingQuestionnaire} from '../api';
import {mergeRankingQuestionnaires, normalizePlayerRankings, playerRankingQuestionnaires, rankingOptionLabel, rankingQuestionLabel, upsertPlayerRanking} from '../constants/player-ranking-questionnaires';
import {getPlayerAffiliationBenefits} from '../util/player-affiliation-benefits';
registerLocale('it', it);

const addDays = (value, days) => {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date;
};

const rankingSportLabels = {
  tennis: 'Tennis',
  padel: 'Padel',
  pickleball: 'Pickleball'
};

const federationDefaultForSport = sport => ({
  federation: ['tennis', 'padel', 'pickleball'].includes(sport) ? 'FITP' : '',
  country: ['tennis', 'padel', 'pickleball'].includes(sport) ? 'IT' : '',
});

const rankingSportKeys = ['tennis', 'padel', 'pickleball'];

const federationStatusLabel = (ranking, t) => {
  if (ranking && ranking.federation && ranking.federation.verified) return t('VERIFIED');
  if (ranking && ranking.federation && ranking.federation.status === 'confirmation_requested') return t('UNDER_REVIEW');
  if (ranking && ranking.federation && ranking.federation.status === 'declared') return t('DECLARED');
  return t('NOT_ENTERED');
};

const RankingTab = ({token, affiliations, partners}) => {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rankings, setRankings] = useState([]);
  const [questionnaires, setQuestionnaires] = useState(playerRankingQuestionnaires);
  const [selectedSport, setSelectedSport] = useState('tennis');
  const [answers, setAnswers] = useState({});
  const [federationData, setFederationData] = useState({});
  const [verificationPartnerId, setVerificationPartnerId] = useState('');
  const [message, setMessage] = useState(null);

  const loadRankings = useCallback(async () => {
    setLoading(true);
    const res = await getPlayerSportRankings(token);
    if (res.status === 200) {
      setRankings(normalizePlayerRankings(res.json.rankings));
      setQuestionnaires(mergeRankingQuestionnaires(res.json.questionnaires));
    } else {
      setRankings(normalizePlayerRankings([]));
      setMessage(t('RANKING_LOAD_FAILED'));
    }
    setLoading(false);
  }, [token, t]);

  useEffect(() => {
    loadRankings();
  }, [loadRankings]);

  useEffect(() => {
    const ranking = rankings.find(item => item.sport === selectedSport);
    const nextAnswers = {};
    (ranking && ranking.answers ? ranking.answers : []).forEach(answer => {
      nextAnswers[answer.questionKey] = answer.value;
    });
    const federationDefaults = federationDefaultForSport(selectedSport);
    setAnswers(nextAnswers);
    setFederationData({
      federation: (ranking && ranking.federation && ranking.federation.federation) || federationDefaults.federation,
      country: (ranking && ranking.federation && ranking.federation.country) || federationDefaults.country,
      licenseNumber: (ranking && ranking.federation && ranking.federation.licenseNumber) || '',
      classification: (ranking && ranking.federation && ranking.federation.classification) || '',
      bestClassification: (ranking && ranking.federation && ranking.federation.bestClassification) || ''
    });
  }, [rankings, selectedSport]);

  const selectedQuestionnaire = questionnaires[selectedSport] || {questions: [], options: []};
  const selectedRanking = rankings.find(item => item.sport === selectedSport);
  const allAnswered = selectedQuestionnaire.questions.every(question => answers[question.key] !== undefined);
  const showFederationFields = ['tennis', 'padel', 'pickleball'].includes(selectedSport);
  const isFitpSport = ['tennis', 'padel', 'pickleball'].includes(selectedSport);
  const verificationPartnerOptions = (affiliations || [])
    .map(affiliation => {
      const affiliationInfo = affiliation.affiliation || affiliation;
      const partnerId = affiliationInfo.partnerId || affiliation.partnerId || affiliationInfo.partner_id || affiliation.partner_id;
      if (!partnerId) return null;
      const partner = (partners || []).find(item => String(item.uid || item.partnerId) === String(partnerId));
      return {
        value: String(partnerId),
        label: (partner && (partner.businessName || partner.name)) ||
          affiliationInfo.partnerName ||
          affiliationInfo.partnerBusinessName ||
          affiliationInfo.partner ||
          t('CLUB_NUMBER', {id: partnerId})
      };
    })
    .filter(Boolean)
    .filter((option, index, options) => options.findIndex(item => item.value === option.value) === index);

  const saveQuestionnaire = async () => {
    if (!allAnswered) {
      setMessage(t('RANKING_COMPLETE_ALL_QUESTIONS'));
      return;
    }

    setSaving(true);
    const payloadAnswers = selectedQuestionnaire.questions.map(question => ({
      questionKey: question.key,
      value: answers[question.key]
    }));
    const res = await savePlayerSportRankingQuestionnaire(selectedSport, payloadAnswers, token);
    if (res.status === 200) {
      setMessage(t('RANKING_UPDATED'));
      if (res.json && res.json.ranking) {
        setRankings(currentRankings => upsertPlayerRanking(currentRankings, res.json.ranking));
      }
      await loadRankings();
    } else {
      setMessage(t('RANKING_SAVE_FAILED', {error: res.json && res.json.error ? res.json.error : res.status}));
    }
    setSaving(false);
  };

  const saveFederation = async () => {
    if (!selectedRanking || !selectedRanking.configured) {
      setMessage(t('RANKING_SELF_ASSESSMENT_REQUIRED_FITP'));
      return;
    }

    setSaving(true);
    const res = await savePlayerSportRankingFederation(selectedSport, federationData, token);
    if (res.status === 200 && res.json && res.json.ranking) {
      setRankings(currentRankings => upsertPlayerRanking(currentRankings, res.json.ranking));
      setMessage(t('RANKING_FEDERATION_UPDATED'));
    } else {
      setMessage(t('RANKING_FEDERATION_SAVE_FAILED', {error: res.json && res.json.error ? res.json.error : res.status}));
    }
    setSaving(false);
  };

  const requestVerification = async () => {
    if (!selectedRanking || !selectedRanking.configured) {
      setMessage(t('RANKING_SELF_ASSESSMENT_REQUIRED'));
      return;
    }
    if (!verificationPartnerId) {
      setMessage(t('RANKING_SELECT_CLUB_CONFIRMATION'));
      return;
    }

    setSaving(true);
    const res = await requestPlayerSportRankingVerification(selectedSport, verificationPartnerId, token);
    if ((res.status === 200 || res.status === 201) && res.json && res.json.ranking) {
      setRankings(currentRankings => upsertPlayerRanking(currentRankings, res.json.ranking));
      setMessage(t('RANKING_CONFIRMATION_REQUEST_SENT'));
    } else {
      const error = res.json && res.json.error ? res.json.error : res.status;
      setMessage(t('RANKING_CONFIRMATION_REQUEST_FAILED', {error}));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="player-ranking-panel">
        <p>{t('RANKING_LOADING')}</p>
      </div>
    );
  }

  return (
    <div className="player-ranking-panel">
      <div className="player-profile-skywork-section-head">
        <span className="player-profile-skywork-section-icon"><FaTrophy aria-hidden="true"/></span>
        <div>
          <h2>{t('RANKING')}</h2>
          <p>{t('RANKING_PROFILE_INTRO')}</p>
        </div>
      </div>
      {message ? <div className="player-ranking-message">{message}</div> : null}
      <div className="player-ranking-guide">
        <strong>{t('RANKING_GUIDE_TITLE')}</strong>
        <p>{t('RANKING_GUIDE_BODY_ONE')}</p>
        <div className="player-ranking-guide-scale">
          {[1, 2, 3, 4, 5].map(index => (
            <div key={index}>
              <span>{t(`RANKING_GUIDE_SCALE_${index}_LEVEL`)}</span>
              <b>{t(`RANKING_GUIDE_SCALE_${index}_LABEL`)}</b>
              <small>{t(`RANKING_GUIDE_SCALE_${index}_FITP`)}</small>
            </div>
          ))}
        </div>
        <p>{t('RANKING_GUIDE_BODY_TWO')}</p>
        <p>{t('RANKING_GUIDE_BODY_THREE')}</p>
      </div>
      <div className="player-ranking-sports">
        {rankings.map(ranking => (
          <button
            key={ranking.sport}
            type="button"
            className={`player-ranking-card ${selectedSport === ranking.sport ? 'active' : ''}`}
            onClick={() => {
              setSelectedSport(ranking.sport);
              setMessage(null);
            }}
          >
            <span>{rankingSportLabels[ranking.sport] || ranking.sport}</span>
            <strong>{ranking.configured ? Number(ranking.displayLevel || ranking.level).toFixed(1) : t('NOT_CONFIGURED')}</strong>
            <small>{ranking.externalLabel || t('SELF_ASSESSMENT_NOT_COMPLETED')}</small>
            <small>{t('RANKING_RELIABILITY_VALUE', {value: ranking.ratingReliability || ranking.reliability || 0})}</small>
          </button>
        ))}
      </div>
      <div className="player-ranking-federation-overview">
        <div className="player-ranking-federation-overview-head">
          <strong>{t('RANKING_FEDERATION_CARDS_TITLE')}</strong>
          <span>{t('RANKING_FEDERATION_CARDS_HELP')}</span>
        </div>
        <div className="player-ranking-federation-cards">
          {rankingSportKeys.map(sport => {
            const ranking = rankings.find(item => item.sport === sport);
            const federation = ranking && ranking.federation ? ranking.federation : {};
            const isFitpSportCard = ['tennis', 'padel', 'pickleball'].includes(sport);
            return (
              <button
                key={sport}
                type="button"
                className={`player-ranking-federation-card ${selectedSport === sport ? 'active' : ''}`}
                onClick={() => {
                  setSelectedSport(sport);
                  setMessage(null);
                }}
              >
                <span>{t('FEDERATION_LICENSE_FOR_SPORT', {sport: rankingSportLabels[sport] || sport})}</span>
                <strong>{federation.licenseNumber || t('NOT_ENTERED')}</strong>
                <small>{isFitpSportCard ? 'FITP' : t('RANKING_FEDERATION_TITLE')} · {federationStatusLabel(ranking, t)}</small>
              </button>
            );
          })}
        </div>
      </div>
      <div className="player-ranking-questionnaire">
        <div className="player-ranking-questionnaire-head">
          <div>
            <span>{t('SELF_ASSESSMENT')}</span>
            <h3>{rankingSportLabels[selectedSport] || selectedSport}</h3>
          </div>
          <div className="player-ranking-current">
            <small>{t('CURRENT_LEVEL')}</small>
            <strong>{selectedRanking && selectedRanking.configured ? Number(selectedRanking.displayLevel || selectedRanking.level).toFixed(1) : '-'}</strong>
            {selectedRanking && selectedRanking.configured ? (
              <span>{selectedRanking.externalLabel || `Rating ${selectedRanking.rating || 0}`}</span>
            ) : null}
          </div>
        </div>
        {selectedQuestionnaire.questions.map(question => (
          <div key={question.key} className="player-ranking-question">
            <strong>{rankingQuestionLabel(question, t)}</strong>
            <div className="player-ranking-options">
              {(question.options || selectedQuestionnaire.options).map(option => (
                <button
                  key={`${question.key}-${option.value}`}
                  type="button"
                  className={answers[question.key] === option.value ? 'active' : ''}
                  onClick={() => setAnswers({...answers, [question.key]: option.value})}
                >
                  {rankingOptionLabel(option, t)}
                </button>
              ))}
            </div>
          </div>
        ))}
        <Button type="button" className="profile-auth-action profile-auth-action--save" disabled={saving} onClick={saveQuestionnaire}>
          <FaSave aria-hidden="true"/>{saving ? t('SAVING') : t('SAVE_RANKING_SPORT', {sport: rankingSportLabels[selectedSport] || selectedSport})}
        </Button>
      </div>
      {showFederationFields ? (
        <div className="player-ranking-federation">
          <div className="player-ranking-questionnaire-head">
            <div>
              <span>{t('OPTIONAL_DATA')}</span>
              <h3>{isFitpSport ? t('RANKING_FITP_TITLE') : t('RANKING_FEDERATION_TITLE')}</h3>
            </div>
            <div className="player-ranking-current">
              <small>{t('STATUS')}</small>
              <strong>{federationStatusLabel(selectedRanking, t)}</strong>
              <span>{t('RANKING_FITP_RELIABILITY_HELP')}</span>
            </div>
          </div>
          <p className="player-ranking-federation-note">{t('RANKING_FITP_OPTIONAL_HELP')}</p>
          <div className="player-ranking-federation-grid">
            <Form.Group>
              <Form.Label>{t('FEDERATION_NAME')}</Form.Label>
              <Form.Control
                type="text"
                value={federationData.federation || ''}
                placeholder={isFitpSport ? 'FITP' : t('OPTIONAL')}
                onChange={event => setFederationData({...federationData, federation: event.target.value})}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('FEDERATION_COUNTRY')}</Form.Label>
              <Form.Control
                type="text"
                value={federationData.country || ''}
                placeholder={isFitpSport ? 'IT' : t('OPTIONAL')}
                onChange={event => setFederationData({...federationData, country: event.target.value})}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{isFitpSport ? t('FITP_LICENSE_NUMBER') : t('FEDERATION_LICENSE_NUMBER')}</Form.Label>
              <Form.Control
                type="text"
                value={federationData.licenseNumber || ''}
                placeholder={t('OPTIONAL')}
                onChange={event => setFederationData({...federationData, licenseNumber: event.target.value})}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{isFitpSport ? t('FITP_CURRENT_CLASSIFICATION') : t('FEDERATION_CURRENT_CLASSIFICATION')}</Form.Label>
              <Form.Control
                type="text"
                value={federationData.classification || ''}
                placeholder={isFitpSport ? (selectedSport === 'pickleball' ? 'Es. 3.2, 2.1, NC' : 'Es. 4.2, 3.5, NC') : t('OPTIONAL')}
                onChange={event => setFederationData({...federationData, classification: event.target.value})}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{isFitpSport ? t('FITP_BEST_CLASSIFICATION') : t('FEDERATION_BEST_CLASSIFICATION')}</Form.Label>
              <Form.Control
                type="text"
                value={federationData.bestClassification || ''}
                placeholder={t('OPTIONAL')}
                onChange={event => setFederationData({...federationData, bestClassification: event.target.value})}
              />
            </Form.Group>
          </div>
          <Button type="button" className="profile-auth-action profile-auth-action--save" disabled={saving} onClick={saveFederation}>
            <FaSave aria-hidden="true"/>{saving ? t('SAVING') : t('SAVE_FEDERATION_DATA')}
          </Button>
          <div className="player-ranking-verification-request">
            <div>
              <strong>{t('CLUB_CONFIRMATION')}</strong>
              <span>{t('CLUB_CONFIRMATION_HELP')}</span>
            </div>
            {verificationPartnerOptions.length > 0 ? (
              <div className="player-ranking-verification-controls">
                <Form.Control
                  as="select"
                  value={verificationPartnerId}
                  onChange={event => setVerificationPartnerId(event.target.value)}
                >
                  <option value="">{t('CHOOSE_CLUB')}</option>
                  {verificationPartnerOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Control>
                <Button type="button" className="profile-auth-action profile-auth-action--save" disabled={saving || !verificationPartnerId} onClick={requestVerification}>
                  <FaIdCard aria-hidden="true"/>{saving ? t('SENDING') : t('REQUEST_CONFIRMATION')}
                </Button>
              </div>
            ) : (
              <p className="player-ranking-federation-note">{t('RANKING_CONFIRMATION_NEEDS_AFFILIATION')}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

RankingTab.propTypes = {
  token: PropTypes.string.isRequired,
  affiliations: PropTypes.array,
  partners: PropTypes.array,
};

RankingTab.defaultProps = {
  affiliations: [],
  partners: [],
};

const PlayerProfile = ({
  profile,
  save,
  deleteProfile,
  close, token,
  removeFile,
  partners, subscriptions,
  changeAutorenew,
  archiveSubscription,
  showReplayerTV,
  createBookingReport,
  createTransactionReport,
  createSubscriptionReport,
  storagePrices,
  changeStorageType,
  isStorageModalVisible,
  showStorageModal,
  hideStorageModal,
  affiliations,
  history,
}) => {
  const {t, i18n} = useTranslation();
  if (profile === null) {
    return (
      <Redirect to="/login"/>
    );
  }

  const imageProfile = (profile.image || []).map(f => {
    return {
      source: f,
      options: {
        type: 'local'
      }
    };
  });

  let dialog = null;
  const onRemove = () => {
    dialog.show({
      title: t('DELETE_PROFILE'),
      body: t('REVOCATION_PERSONAL_DATA_PROCESSING'),
      actions: [
        Dialog.Action(
          t('YES_DELETE'),
          () => deleteProfile(),
          'btn btn-secondary'
        ),

        Dialog.Action(
          t('NO_CANCEL'),
          null,
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const confirmArchiveSubscription = subscription => () => {
    dialog.show({
      title: t('CONFIRM_ARCHIVE_TITLE'),
      body: t('CONFIRM_ARCHIVE_SUBSCRIPTION'),
      actions: [
        Dialog.Action(
          t('ARCHIVE_BUTTON'),
          () => archiveSubscription(subscription),
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('CANCEL'),
          null,
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const onSubmit = profile => {
    if (profile.privacy0) {
      save(profile);
    } else {
      onRemove();
    }
  };

  const findPartner = partnerId => partners.find(p => p.uid === partnerId);

  const storageLimit = {
    xs: 20,
    s: 30,
    m: 50,
    l: 100,
    xl: 200,
    xxl: 500,
    xxxl: 1000,
  }

  const selectableStorageType = profile.storageType === "xs" ? "s" : profile.storageType;

  const getRtvChannel = (partner, rtvChannelId) => {
    return partner.rtvChannels.find(c => c.id === rtvChannelId);    
  }

  const getRtvName = (partner, rtvChannelId) => {
    const channel = partner.rtvChannels.find(c => c.id === rtvChannelId);
    if (channel) {
      return channel.name;
    } else {
      return null;
    }
  }

  const IsAffiliationSubscriptionExpired = affiliation => {
    if (affiliation.affiliation.type === affiliationType.rtv) {
      const subscription = subscriptions.find(s => s.rtv_channel_id === affiliation.affiliation.rtvChannelId && s.kind === subscriptionKind.rtvDedicated)
      return subscription && Date.parse(subscription.expiry) < Date.now();
    }

    return false;
  }

  const renderAffiliation = affiliation => {
    const partner = findPartner(affiliation.affiliation.partnerId);
    const rtvChannel = partner && affiliation.affiliation.rtvChannelId ? getRtvChannel(partner, affiliation.affiliation.rtvChannelId) : null;
    const benefits = getPlayerAffiliationBenefits(affiliation);

    const benefitLabel = benefit => {
      if (benefit.type === 'calendar') {
        return t('CLUB_SKYWORK_AFFILIATION_CALENDAR_VISIBILITY', {days: benefit.days});
      }
      if (benefit.type === 'discount') {
        return t('CLUB_SKYWORK_AFFILIATION_DISCOUNT', {discount: benefit.discount});
      }
      if (benefit.type === 'sport_discount') {
        return t('PLAYER_AFFILIATION_SPORT_DISCOUNT', {
          sport: benefit.sport,
          discount: benefit.discount
        });
      }
      return t('PLAYER_AFFILIATION_DEDICATED_RATES', {sports: benefit.sports});
    };

    const benefitIcon = benefit => {
      if (benefit.type === 'calendar') return <FaCalendarAlt aria-hidden="true"/>;
      if (benefit.type === 'dedicated_rates') return <FaTags aria-hidden="true"/>;
      return <FaPercent aria-hidden="true"/>;
    };

    return (
      <div className="row">
        <div className="col-md-12 col-lg-12 mb-2 mb-md-12 card-group">
          <div className={`${rtvChannel && rtvChannel.type === 1 ? "card video_store_bg_rp_specialist ease_fx" : "card video_store_bg_rp ease_fx"} player-affiliation-card`}>
            <div className="card-horizontal">
              <div className="col-sm-3 col-lg-4 pl-0 pr-0 video_store_logo_bg_rp d-flex align-items-stretch justify-content-center">
              {
                rtvChannel && rtvChannel.logoNames && rtvChannel.logoNames.length > 0 ?
                  <div className="align-self-center">
                    <img
                      className="img-fluid"
                      src={`${Configuration.apiEndPoint}/partners/rtv_channels/${rtvChannel.id}/images?load=${rtvChannel.logoNames[0]}`}
                      alt={t('LOGO')}
                    />
                  </div> :
                    partner && partner.storeImage && partner.storeImage.length > 0 ?
                      <div className="align-self-center">
                        <img
                          className="img-fluid"
                          src={`${Configuration.apiEndPoint}/images/${partner.uid}?load=${partner.storeImage[0]}`}
                          alt={t('LOGO')}
                        />
                      </div> :
                      <div className="align-self-center video_store_nologo_overlay_rp"/>
                }
              </div>
              <div className="card-body align-items-center col-sm-9 col-lg-8 player-affiliation-card-body" style={!affiliation.active || !affiliation.affiliation.active || IsAffiliationSubscriptionExpired(affiliation) ? {background:"#505A6F"} : {}}>
                <h5 className="card-title mb-0">{partner ? partner.name : null}</h5>
                <Badge variant={affiliation.active && affiliation.affiliation.active && !IsAffiliationSubscriptionExpired(affiliation) ? "success" : "danger"}>{affiliation.affiliation.name}</Badge>
                {
                  affiliation.active === null ?
                  <p className="card-text note_card_rp">{t("GROUP_MEMBERSHIP_PENDING_APPROVAL")}</p> :
                  null
                }   
                {
                  affiliation.active === false || !affiliation.affiliation.active || IsAffiliationSubscriptionExpired(affiliation) ?
                  <p className="card-text note_card_rp">{t("GROUP_MEMBERSHIP_DISABLED_BY_PARTNER")}</p> :
                  null
                }
                {
                  affiliation.affiliation.renewalType !== renewalType.lifeTime && affiliation.expiryTimestamp ?
                  <p className="card-text note_card_rp" style={{color: new Date(affiliation.expiryTimestamp) < new Date() ? 'red' : 'white'}}>{t('EXPIRY_DATE', {date: new Date(affiliation.expiryTimestamp).toLocaleDateString()})}</p>
                  : null
                }
                {
                  affiliation.active && affiliation.affiliation.onlinePayment !== null ?
                    affiliation.payed ?
                      <p className="card-text note_card_rp">{t('PAID')}</p> :
                      <p className="card-text note_card_rp">{t('TO_PAY')}</p>
                  : null
                }
                <p className="card-text note_card_rp">{affiliation.affiliation.description}</p>
                {benefits.length > 0 ? (
                  <div className="player-affiliation-benefits" aria-label={t('PLAYER_AFFILIATION_BENEFITS')}>
                    <strong className="player-affiliation-benefits-title">{t('PLAYER_AFFILIATION_BENEFITS')}</strong>
                    <div className="player-affiliation-benefits-list">
                      {benefits.map(benefit => (
                        <div className="player-affiliation-benefit" key={benefit.id}>
                          <span className="player-affiliation-benefit-icon">{benefitIcon(benefit)}</span>
                          <span>{benefitLabel(benefit)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSubscription = subscription => {
    const partner = findPartner(subscription.partner);
    const rtvChannel = partner && subscription.rtv_channel_id ? getRtvChannel(partner, subscription.rtv_channel_id) : null;
    const hasUpgraded = subscriptions.find(s => new Date(s.expiry) >= new Date() && (s.kind === subscriptionKind.rtvUnlimited || (s.kind === subscriptionKind.rtvDedicated && partner && s.partner === partner.uid && s.rtv_channel_id === subscription.rtv_channel_id)));
    if (subscription.archived)
      return null
  
    return (
      <div className="row">
        <div className="col-md-12 col-lg-12 mb-2 mb-md-12 card-group">
          {
            Date.parse(subscription.expiry) < Date.now() ?
              <div className={rtvChannel && rtvChannel.type === 1 ? "card video_store_bg_rp_specialist ease_fx" :"card video_store_bg_rp ease_fx"}>
                <div className="card-horizontal">
                  <div className="col-sm-3 col-lg-4 pl-0 pr-0 video_store_logo_bg_rp d-flex align-items-stretch justify-content-center">
                    {
                      rtvChannel && rtvChannel.logoNames && rtvChannel.logoNames.length > 0 ?
                      <div className="align-self-center">
                        <img
                          className="img-fluid"
                          src={`${Configuration.apiEndPoint}/partners/rtv_channels/${rtvChannel.id}/images?load=${rtvChannel.logoNames[0]}`}
                          alt={t('LOGO')}
                        />
                      </div> :
                      partner && partner.storeImage && partner.storeImage.length > 0 ?
                        <div className="align-self-center">
                          <img
                            className="img-fluid"
                            src={`${Configuration.apiEndPoint}/images/${partner.uid}?load=${partner.storeImage[0]}`}
                            alt={t('LOGO')}
                          />
                        </div> :
                        <div className="align-self-center video_store_nologo_overlay_rp"/>
                    }
                  </div>
                  <div className="card-body align-items-center col-sm-9 col-lg-8" style={{background: "#505A6F"}}>
                    <h5 className="card-title mb-0">{subscription.kind === subscriptionKind.rtvEvent ? t('REPLAYER_TV_SPECIAL_EVENT_SUBSCRIPTION') : null}</h5>
                    {
                      partner && subscription.rtv_channel_id ?
                        <h5 className="card-title mb-0" style={{ color: '#fff'}}>{getRtvName(partner, subscription.rtv_channel_id)}</h5> :
                        <h5 className="card-title mb-0">{partner ? partner.name : t('REPLAYER_TV_UNLIMITED')}</h5>
                    }
                    <Badge variant="danger">{subscription.kind === subscriptionKind.rtvUnlimited ? t('UNLIMITED') : subscription.kind === subscriptionKind.rtvDedicated ? t('DEDICATED') : t('SPECIAL_EVENT')}</Badge>
                    <Badge variant="dark">{subscription.cost / 100} €</Badge>
                    <p className="card-text note_card_rp">{t('STARTING_DATE', {date: new Date(subscription.created).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})}</p>
                    <p className="card-text note_card_rp">{t('EXPIRED_DATE', {date: new Date(subscription.expiry).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})}</p>
                    <Button className="profile-auth-action profile-auth-action--archive" onClick={confirmArchiveSubscription(subscription)} style={{marginRight: 10, marginBottom: 10}}><FaArchive aria-hidden="true"/>{t('ARCHIVE_BUTTON')}</Button>
                    {
                      !hasUpgraded ?
                        <Button className="profile-auth-action profile-auth-action--renew" onClick={showReplayerTV(partner, rtvChannel)} style={{marginBottom: 10}}><FaSyncAlt aria-hidden="true"/>{t('RENEW')}</Button>
                      : null
                    }
                  </div>
                </div>
              </div>
            :
              subscription.kind !== subscriptionKind.rtvEvent ?
                <div className={rtvChannel && rtvChannel.type === 1 ? "card video_store_bg_rp_specialist ease_fx" : "card video_store_bg_rp ease_fx"}>
                  <div className="card-horizontal">
                    <div className="col-sm-3 col-lg-4 pl-0 pr-0 video_store_logo_bg_rp d-flex align-items-stretch justify-content-center">
                      {
                        rtvChannel && rtvChannel.logoNames && rtvChannel.logoNames.length > 0 ?
                        <div className="align-self-center">
                          <img
                            className="img-fluid"
                            src={`${Configuration.apiEndPoint}/partners/rtv_channels/${rtvChannel.id}/images?load=${rtvChannel.logoNames[0]}`}
                            alt={t('LOGO')}
                          />
                        </div> :
                        partner && partner.storeImage && partner.storeImage.length > 0 ?
                          <div className="align-self-center">
                            <img
                              className="img-fluid"
                              src={`${Configuration.apiEndPoint}/images/${partner.uid}?load=${partner.storeImage[0]}`}
                              alt={t('LOGO')}
                            />
                          </div> :
                          <div className="align-self-center video_store_nologo_overlay_rp"/>
                      }
                    </div>
                    <div className="card-body align-items-center col-sm-9 col-lg-8">
                      {
                        partner && subscription.rtv_channel_id ?
                          <h5 className="card-title mb-0" style={{ color: '#fff'}}>{getRtvName(partner, subscription.rtv_channel_id)}</h5> :
                          <h5 className="card-title mb-0">{partner ? partner.name : t('REPLAYER_TV_UNLIMITED')}</h5>
                      }
                      <Badge variant="success">{subscription.kind === subscriptionKind.rtvUnlimited ? t('UNLIMITED') : t('DEDICATED')}</Badge>
                      <Badge variant="dark">{subscription.cost / 100} €</Badge>
                      <Form.Group>
                        <Form.Check name="autorenew" type="checkbox" label={t('SUBSCRIPTION_AUTORENEW')} checked={subscription.autorenew} onChange={changeAutorenew(subscription)}/>
                      </Form.Group>
                      <p className="card-text note_card_rp">{t('STARTING_DATE', {date: new Date(subscription.created).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})}</p>
                      <p className="card-text note_card_rp">{t('EXPIRY_DATE', {date: new Date(subscription.expiry).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})}</p>
                    </div>
                  </div>
                </div>
              :
                <div className="card video_store_bg_rp_special_event ease_fx">
                  <div className="card-horizontal">
                    <div className="col-sm-3 col-lg-4 pl-0 pr-0 video_store_logo_bg_rp d-flex align-items-stretch justify-content-center">
                      {
                        rtvChannel && rtvChannel.logoNames && rtvChannel.logoNames.length > 0 ?
                        <div className="align-self-center">
                          <img
                            className="img-fluid"
                            src={`${Configuration.apiEndPoint}/partners/rtv_channels/${rtvChannel.id}/images?load=${rtvChannel.logoNames[0]}`}
                            alt={t('LOGO')}
                          />
                        </div> :
                        partner && partner.storeImage && partner.storeImage.length > 0 ?
                          <div className="align-self-center">
                            <img
                              className="img-fluid"
                              src={`${Configuration.apiEndPoint}/images/${partner.uid}?load=${partner.storeImage[0]}`}
                              alt={t('LOGO')}
                            />
                          </div> :
                          <div className="align-self-center video_store_nologo_overlay_rp"/>
                      }
                    </div>
                    {
                      !hasUpgraded ?
                        <div className="card-body align-items-center col-sm-9 col-lg-8">
                          <h5 className="card-title mb-0">{t('REPLAYER_TV_SPECIAL_EVENT_SUBSCRIPTION')}</h5>
                          <h5 className="card-title mb-0">{getRtvName(partner, subscription.rtv_channel_id)}</h5>
                          <Badge variant="success">{t('SPECIAL_EVENT')}</Badge>
                          <Badge variant="dark">{subscription.cost / 100} €</Badge>
                          <p className="card-text note_card_rp">{t('STARTING_DATE', {date: new Date(subscription.created).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})}</p>
                          <p className="card-text note_card_rp">{t('EXPIRY_DATE', {date: new Date(subscription.expiry).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})}</p>
                          <Button className="profile-auth-action profile-auth-action--upgrade" onClick={showReplayerTV(partner, rtvChannel)}>
                            <FaArrowUp aria-hidden="true"/>{t('UPGRADE')}
                          </Button>
                        </div>
                      :
                        <div className="card-body align-items-center col-sm-9 col-lg-8" style={{background:"#505A6F"}}>
                          <h5 className="card-title mb-0">{t('REPLAYER_TV_SPECIAL_EVENT_SUBSCRIPTION')}</h5>
                          <h5 className="card-title mb-0">{getRtvName(partner, subscription.rtv_channel_id)}</h5>
                          <Badge variant="success">{t('SPECIAL_EVENT')}</Badge>
                          <Badge variant="dark">{subscription.cost / 100} €</Badge>
                          <p className="card-text note_card_rp">{t('STARTING_DATE', {date: new Date(subscription.created).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})}</p>
                          <p className="card-text note_card_rp">{t('EXPIRY_DATE', {date: new Date(subscription.expiry).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})}</p>
                        </div>
                    }
                  </div>
                </div>
              }
        </div>
      </div>
    );
  };

  const subscriptionsMinusAffiliations = subscriptions.filter(s => {
    if (!s.rtv_channel_id) {
      return true;
    }

    return !affiliations.some(a => a.affiliation.type === affiliationType.rtv && a.affiliation.rtvChannelId === s.rtv_channel_id && a.active && a.affiliation.active);
  })

  return (
    <Fragment>
      <CommonMetaTag/>
      <Fade in appear className="player-profile-skywork-page" style={{transition: 'opacity 0.30s linear', marginTop: 100, marginBottom: 100}}>
        <Container className="player-profile-skywork-shell">
          <div className="player-profile-skywork-hero">
            <span className="player-profile-skywork-eyebrow"><FaUser aria-hidden="true"/> {t('PROFILE_AREA_PERSONAL')}</span>
            <h1>{t('PROFILE')}</h1>
            <p>{t('PROFILE_HERO_DESCRIPTION')}</p>
          </div>
          <Row className="justify-content-center">
            <Col xs={12}>
              <Tabs defaultActiveKey="profile" className="player-profile-tabs">
                <Tab eventKey="profile" title={<span className="player-profile-tab-label"><FaUser aria-hidden="true"/>{t('PROFILE')}</span>} tabClassName="player-profile-tab">
                  <div className="player-profile-skywork-panel">
                  <div className="player-profile-skywork-section-head">
                    <span className="player-profile-skywork-section-icon"><FaUser aria-hidden="true"/></span>
                    <div><h2>{t('PROFILE_PERSONAL_DATA_TITLE')}</h2><p>{t('PROFILE_PERSONAL_DATA_BODY')}</p></div>
                  </div>
                  <div className="player-profile-skywork-photo-card">
                  <Form.Label style={{marginTop: 20}}>{t('PROFILE_PHOTO')}</Form.Label>
                    <FilePond
                      files={imageProfile}
                      server={
                        {
                          url: `${Configuration.apiEndPoint}/`,
                          process: {
                            url: './images',
                            ondata: formData => {
                              formData.append('image_type', imageTypes.profile);
                              return formData;
                            }
                          },
                          revert: './images',
                          restore: './images',
                          fetch: './images',
                          load: `./images/${profile.uid}?load=`,
                          headers: {Authorization: `Bearer ${token}`},
                          remove: (source, load) => {
                            removeFile(source);
                            load();
                          }
                        }
                      }
                    />
                    <Form.Text className="text-muted">{t('IMAGES_SIZE', {size1: '512', size2: '512'})}</Form.Text>
                  </div>
                  <Formik
                    enableReinitialize
                    initialValues={profile}
                    onSubmit={onSubmit}
                  >
                    {({
                      handleSubmit,
                      handleChange,
                      setFieldValue,
                      values
                    }) => {
                      return (
                        <Form className="player-profile-skywork-form" style={{marginTop: 25}} onSubmit={handleSubmit}>
                          <Form.Group>
                            <Form.Label>{t('NICKNAME')}*</Form.Label>
                            <Form.Control required name="nickname" placeholder={t('NICKNAME')} type="text" maxLength={50} value={values.nickname || ''} onChange={handleChange}/>
                            <Form.Text className="text-muted">
                              {t('NICKNAME')}
                            </Form.Text>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>{t('NAME')}*</Form.Label>
                            <Form.Control required name="name" placeholder={t('NAME')} type="text" maxLength={50} value={values.name || ''} onChange={handleChange}/>
                            <Form.Text className="text-muted">
                              {t('NAME')}
                            </Form.Text>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>{t('SURNAME')}*</Form.Label>
                            <Form.Control required name="surname" type="text" placeholder={t('SURNAME')} maxLength={50} value={values.surname || ''} onChange={handleChange}/>
                            <Form.Text className="text-muted">
                              {t('SURNAME')}
                            </Form.Text>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>{t('GENDER')}</Form.Label>
                            <Form.Control as="select" name="gender" value={values.gender || ''} onChange={handleChange}>
                              <option value="">{t('PROFILE_GENDER_NOT_SPECIFIED')}</option>
                              <option value="male">{t('PROFILE_GENDER_MALE')}</option>
                              <option value="female">{t('PROFILE_GENDER_FEMALE')}</option>
                            </Form.Control>
                            <Form.Text className="text-muted">
                              {t('PROFILE_GENDER_HELP')}
                            </Form.Text>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>{t('BIRTH_DATE')}*</Form.Label><br/>
                            <DatePicker
                              required
                              readOnly
                              showMonthDropdown
                              showYearDropdown
                              peekNextMonth
                              locale="it"
                              className="form-control"
                              dateFormat="dd/MM/yyyy"
                              dropdownMode="select"
                              maxDate={new Date()}
                              selected={(values.birthDate && new Date(values.birthDate)) || null}
                              onChange={val => setFieldValue('birthDate', val)}/>
                            <Form.Text className="text-muted">
                            {t('BIRTH_DATE')}
                            </Form.Text>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>{t('EMAIL')}*</Form.Label>
                            <Form.Control required name="user.email" type="email" placeholder={t('EMAIL')} value={values.user.email || ''} onChange={handleChange}/>
                            <Form.Text className="text-muted">
                            {t('EMAIL')}
                            </Form.Text>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>{t('TELEPHONE')}*</Form.Label>
                            <Form.Control required name="phoneNumber" type="tel" placeholder={t('TELEPHONE')} value={values.phoneNumber || ''} onChange={handleChange}/>
                            <Form.Text className="text-muted">
                            {t('TELEPHONE')}
                            </Form.Text>
                          </Form.Group>
                          <hr style={{height: '1px', background: '#dd004a', color: '#dd004a', borderColor: '#dd004a'}}/>
                          <h4>{t('TAX_DATA')}</h4>
                          <Form.Label>{t('TAX_DATA_INFO')}</Form.Label>
                          <Form.Group>
                            <Form.Label>{t('BUY_CREDIT_AS')}</Form.Label>
                            <Form.Check name="isBusiness" type="radio" label={t('PRIVATE')} checked={values.isBusiness === false} onChange={() => setFieldValue('isBusiness', false)}/>
                            <Form.Check name="isBusiness" type="radio" label={t('BUSINESS')} checked={values.isBusiness === true} onChange={() => setFieldValue('isBusiness', true)}/>
                          </Form.Group>
                          { values.isBusiness === false ?
                          <Form.Group>
                            <Form.Group>
                              <Form.Label>{t('COUNTRY')}*</Form.Label>
                              <Form.Control name="country" type="text" placeholder={t('COUNTRY')} value={values.country || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('COUNTRY')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('ADDRESS')}*</Form.Label>
                              <Form.Control name="address" type="text" placeholder={t('ADDRESS_INFO')} value={values.address || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('ADDRESS_INFO')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('CITY')}*</Form.Label>
                              <Form.Control name="city" type="text" placeholder={t('CITY_INFO')} value={values.city || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('CITY_INFO')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('PROVINCE')}*</Form.Label>
                              <Form.Control name="province" type="text" placeholder={t('PROVINCE_INFO')} value={values.province || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('PROVINCE_INFO')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('CAP')}*</Form.Label>
                              <Form.Control name="cap" type="text" placeholder={t('CAP')} value={values.cap || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('CAP')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('FISCAL_CODE')}*</Form.Label>
                              <Form.Control name="fiscalCode" type="text" placeholder={t('FISCAL_CODE')} value={values.fiscalCode || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('FISCAL_CODE')}
                              </Form.Text>
                            </Form.Group>
                          </Form.Group>
                          : values.isBusiness === true ?
                          <Form.Group>
                            <Form.Group>
                              <Form.Label>{t('BUSINESS_NAME')}</Form.Label>
                              <Form.Control name="businessName" placeholder={t('BUSINESS_NAME')} type="text" maxLength="255" value={values.businessName || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('BUSINESS_NAME')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('COUNTRY')}</Form.Label>
                              <Form.Check name="country" type="radio" label={t('ITALY')} checked={values.country === "Italia"} onChange={() => setFieldValue('country', "Italia")}/>
                              <Form.Check name="country" type="radio" label={t('OTHER')} checked={values.country !== "Italia" && values.country !== null} onChange={() => setFieldValue('country', "")}/>
                            </Form.Group>
                            {values.country !== "Italia" && values.country !== null ?
                            <Form.Group>
                              <Form.Label>{t('COUNTRY')}*</Form.Label>
                              <Form.Control name="country" type="text" placeholder={t('COUNTRY')} value={values.country || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('COUNTRY')}
                              </Form.Text>
                            </Form.Group>
                            : null}
                            <Form.Group>
                              <Form.Label>{t('REGISTERED_OFFICE')}</Form.Label>
                              <Form.Control name="registeredOffice" placeholder={t('ADDRESS_INFO')} type="text" maxLength="255" value={values.registeredOffice || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('ADDRESS_INFO')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('CITY')}*</Form.Label>
                              <Form.Control name="city" type="text" placeholder={t('CITY_INFO')} value={values.city || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('CITY_INFO')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('PROVINCE')}*</Form.Label>
                              <Form.Control name="province" type="text" placeholder={t('PROVINCE_INFO')} value={values.province || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('PROVINCE_INFO')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('CAP')}*</Form.Label>
                              <Form.Control name="cap" type="text" placeholder={t('CAP')} value={values.cap || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('CAP')}
                              </Form.Text>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>{t('VAT_NUMBER')}</Form.Label>
                              <Form.Control name="IVA" placeholder={t('VAT_NUMBER')} type="text" maxLength="20" value={values.IVA || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('VAT_NUMBER')}
                              </Form.Text>
                            </Form.Group>
                            {values.country === "Italia" ?
                            <Form.Group>
                              <Form.Label>{t('SDI_PEC')}</Form.Label>
                              <Form.Control name="sdiPec" placeholder={t('SDI_PEC')} type="text" maxLength="255" value={values.sdiPec || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('SDI_PEC')}
                              </Form.Text>
                            </Form.Group>
                            : null}
                          </Form.Group>
                          : null
                          }
                          <hr style={{height: '1px', background: '#dd004a', color: '#dd004a', borderColor: '#dd004a'}}/>
                          <h4>{t('PRIVACY')}</h4>
                          <div className="player-profile-privacy-panel">
                            <Form.Label>{t('PRIVACY_AUTHORIZATIONS')}<a href="http://www.replayer.it" target="_blank" rel="noopener noreferrer"> www.replayer.it </a>{t('LEGAL_INFO')}
                            </Form.Label>
                            <Form.Group>
                              <Form.Check name="privacy0" type="checkbox" label={t('PRIVACY0', {date: new Date(values.privacy0Datetime).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})} checked={values.privacy0} onChange={handleChange}/>
                            </Form.Group>
                            <Form.Group>
                              <Form.Check name="privacy1" type="checkbox" label={t('PRIVACY1', {date: new Date(values.privacy1Datetime).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})} checked={values.privacy1} onChange={handleChange}/>
                            </Form.Group>
                            <Form.Group>
                              <Form.Check name="privacy2" type="checkbox" label={t('PRIVACY2', {date: new Date(values.privacy2Datetime).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})} checked={values.privacy2} onChange={handleChange}/>
                            </Form.Group>
                            <Form.Group>
                              <Form.Check name="privacy3" type="checkbox" label={t('PRIVACY3', {date: new Date(values.privacy3Datetime).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})} checked={values.privacy3} onChange={handleChange}/>
                            </Form.Group>
                            <Form.Group>
                              <Form.Check name="privacy4" type="checkbox" label={t('PRIVACY4', {date: new Date(values.privacy4Datetime).toLocaleDateString(i18n.language, {year: 'numeric', month: 'numeric', day: 'numeric'})})} checked={values.privacy4} onChange={handleChange}/>
                            </Form.Group>
                          </div>
                          <Button type="submit" className="profile-auth-action profile-auth-action--save"><FaSave aria-hidden="true"/>{t('SAVE')}</Button>
                          <Button type="button" className="profile-auth-action profile-auth-action--close" style={{marginLeft: 10}} onClick={close}><FaTimes aria-hidden="true"/>{t('CLOSE_BUTTON')}</Button>
                        </Form>
                      );
                    }}
                  </Formik>
                  <AuthMethods token={token}/>
                  </div>
                </Tab>
                <Tab eventKey="ranking" title={<span className="player-profile-tab-label"><FaTrophy aria-hidden="true"/>{t('RANKING')}</span>} tabClassName="player-profile-tab" style={{marginTop: 20}}>
                  <RankingTab token={token} affiliations={affiliations} partners={partners}/>
                </Tab>
                <Tab eventKey="resources" title={<span className="player-profile-tab-label"><FaWallet aria-hidden="true"/>{t('RESOURCES')}</span>} tabClassName="player-profile-tab" style={{marginTop: 20}}>
                  <div className="player-resources-skywork-grid">
                    <div className="partner-profile-card player-resources-skywork-storage-card">
                      <div className="partner-profile-section-title">
                        <span>{t('VIDEO_ARCHIVE')}</span>
                        <h5>{t('MY_VIDEO_SPACE')}</h5>
                        <p>{t('VIDEO_ARCHIVE_DESCRIPTION')}</p>
                      </div>
                      <div className="player-resources-skywork-quota"><FaDatabase aria-hidden="true"/><div className="player-resources-skywork-quota-values"><strong>{(Math.floor(profile.videoQuota)/(1000000000)).toFixed(2)}GB</strong><span>di {storageLimit[profile.storageType]}GB</span></div></div>
                      <button type="button" className="btn myvideo-action myvideo-action--utility" onClick={showStorageModal}><FaDatabase aria-hidden="true"/>{t('MANAGE_SPACE')}</button>
                    </div>
                  <Container fluid className="player-resources-skywork-wallet-wrap">
                    <div className="partner-profile-card partner-profile-wallet-card">
                      <div className="partner-profile-section-title">
                        <span>{t('CREDIT')}</span>
                        <h5>{t('MY_CREDIT')}</h5>
                        <p>{t('CREDIT_DESCRIPTION')}</p>
                      </div>
                      <Form.Group>
                        <Form.Label>{t('CREDIT_VALUE', {value: profile.user.wallet})}</Form.Label>
                      </Form.Group>
                      <p>{t('TOPUP_DESCRIPTION')}</p>
                      <Button type="button" className="myvideo-action myvideo-action--utility" onClick={() => history.push('/user-top-up-account')}>
                        <FaWallet aria-hidden="true"/>{t('ACCOUNT_TOPUP')}
                      </Button>
                      <div className="partner-profile-warning-card">
                        <h4>{t('WARNING')}</h4>
                        <p>{t('ACCOUNT_TOPUP_NOTES')}</p>
                      </div>
                    </div>
                  </Container>
                  </div>
                </Tab>
                <Tab eventKey="memberships" title={<span className="player-profile-tab-label"><FaIdCard aria-hidden="true"/>{t('MEMBERSHIPS')}</span>} tabClassName="player-profile-tab" style={{marginTop: 20}}>
                  {affiliations.map(a => renderAffiliation(a))}
                  {affiliations.length === 0 ? t('NO_AFFILIATIONS') : null}
                </Tab>
                <Tab eventKey="subscriptions" title={<span className="player-profile-tab-label"><FaTicketAlt aria-hidden="true"/>{t('SUBSCRIPTIONS')}</span>} tabClassName="player-profile-tab" style={{marginTop: 20}}>
                  {subscriptionsMinusAffiliations.map(g => renderSubscription(g))}
                  {subscriptionsMinusAffiliations.length === 0 ? t('NO_SUBSCRIPTIONS') : null}
                </Tab>
                <Tab eventKey="archives" title={<span className="player-profile-tab-label"><FaArchive aria-hidden="true"/>{t('ARCHIVES')}</span>} tabClassName="player-profile-tab" style={{marginTop: 20}}>
                  <div className="player-archives-skywork">
                    <div className="player-profile-skywork-section-head">
                      <span className="player-profile-skywork-section-icon"><FaArchive aria-hidden="true"/></span>
                      <div><h2>{t('ARCHIVES')}</h2><p>{t('ARCHIVES_DESCRIPTION')}</p></div>
                    </div>
                  <Formik
                    enableReinitialize
                    initialValues={{start: null, end: null}}
                    onSubmit={createTransactionReport}
                  >
                    {({
                      handleSubmit,
                      setFieldValue,
                      values
                    }) => {
                      return (
                        <Fragment>
                          <CommonMetaTag/>
                          <Container className="player-archive-report-card">
                            <Row className="justify-content-center" style={{marginTop: 20, marginBottom: 20}}>
                              <Row className="justify-content-center">
                                <h4 style={{marginTop: 30}}>{t('REGISTER_TRANSACTIONS')}</h4>
                              </Row>
                              <Row className="justify-content-center">
                                <p style={{marginTop: 30, textAlign: 'center'}}>{t('REGISTER_TRANSACTIONS_INFO')}</p>
                              </Row>
                              <Row className="justify-content-center">
                                <p style={{marginTop: 30, textAlign: 'center'}}>{t('SELECT_PERIOD')}</p>
                              </Row>
                              <Form className="player-archive-report-form" onSubmit={handleSubmit}>
                                <Form.Row>
                                  <Col>
                                    <Form.Group>
                                      <Form.Label>{t('START_DATE')}</Form.Label><br/>
                                      <DatePicker
                                        locale="it"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        selected={(values.start && new Date(values.start)) || null}
                                        onChange={val => setFieldValue('start', val ? new Date(val).toISOString() : null)}/>
                                    </Form.Group>
                                  </Col>
                                  <Col>
                                    <Form.Group>
                                      <Form.Label>{t('END_DATE')}</Form.Label><br/>
                                      <DatePicker
                                        locale="it"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        selected={(values.end && addDays(values.end, -2)) || null}
                                        onChange={val => setFieldValue('end', val ? addDays(val, 2).toISOString() : null)}/>
                                    </Form.Group>
                                  </Col>
                                </Form.Row>
                                <Button type="submit" className="profile-auth-action profile-auth-action--excel" style={{marginTop: 50, marginBottom: 50}}><FaFileExcel aria-hidden="true"/>{t('EXTRACT_XLS')}</Button>
                              </Form>
                            </Row>
                          </Container>
                        </Fragment>
                      );
                    }}
                  </Formik>
                  <Formik
                    enableReinitialize
                    initialValues={{start: null, end: null}}
                    onSubmit={createBookingReport}
                  >
                    {({
                      handleSubmit,
                      setFieldValue,
                      values
                    }) => {
                      return (
                        <Fragment>
                          <CommonMetaTag/>
                          <Container className="player-archive-report-card">
                            <Row className="justify-content-center" style={{marginTop: 20, marginBottom: 20}}>
                              <Row className="justify-content-center">
                                <h4 style={{marginTop: 30}}>{t('REGISTER_ARCHIVED_RESERVATIONS')}</h4>
                              </Row>
                              <Row className="justify-content-center">
                                <p style={{marginTop: 30, textAlign: 'center'}}>{t('REGISTER_ARCHIVED_RESERVATIONS_INFO')}</p>
                              </Row>
                              <Row className="justify-content-center">
                                <p style={{marginTop: 30, textAlign: 'center'}}>{t('SELECT_PERIOD')}</p>
                              </Row>
                              <Form className="player-archive-report-form" onSubmit={handleSubmit}>
                                <Form.Row>
                                  <Col>
                                    <Form.Group>
                                      <Form.Label>{t('START_DATE')}</Form.Label><br/>
                                      <DatePicker
                                        locale="it"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        selected={(values.start && new Date(values.start)) || null}
                                        onChange={val => setFieldValue('start', val ? new Date(val).toISOString() : null)}/>
                                    </Form.Group>
                                  </Col>
                                  <Col>
                                    <Form.Group>
                                      <Form.Label>{t('END_DATE')}</Form.Label><br/>
                                      <DatePicker
                                        locale="it"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        selected={(values.end && new Date(values.end)) || null}
                                        onChange={val => setFieldValue('end', val ? new Date(val).toISOString() : null)}/>
                                    </Form.Group>
                                  </Col>
                                </Form.Row>
                                <Button type="submit" className="profile-auth-action profile-auth-action--excel" style={{marginTop: 50, marginBottom: 50}}><FaFileExcel aria-hidden="true"/>{t('EXTRACT_XLS')}</Button>
                              </Form>
                            </Row>
                          </Container>
                        </Fragment>
                      );
                    }}
                  </Formik>
                  <Formik
                    enableReinitialize
                    initialValues={{start: null, end: null}}
                    onSubmit={createSubscriptionReport}
                  >
                    {({
                      handleSubmit,
                      setFieldValue,
                      values
                    }) => {
                      return (
                        <Fragment>
                          <CommonMetaTag/>
                          <Container className="player-archive-report-card">
                            <Row className="justify-content-center" style={{marginTop: 20, marginBottom: 20}}>
                              <Row className="justify-content-center">
                                <h4 style={{marginTop: 30}}>{t('REGISTER_ARCHIVED_SUBSCRIPTIONS')}</h4>
                              </Row>
                              <Row className="justify-content-center">
                                <p style={{marginTop: 30, textAlign: 'center'}}>{t('REGISTER_ARCHIVED_SUBSCRIPTIONS_INFO')}</p>
                              </Row>
                              <Row className="justify-content-center">
                                <p style={{marginTop: 30, textAlign: 'center'}}>{t('SELECT_PERIOD')}</p>
                              </Row>
                              <Form className="player-archive-report-form" onSubmit={handleSubmit}>
                                <Form.Row>
                                  <Col>
                                    <Form.Group>
                                      <Form.Label>{t('START_DATE')}</Form.Label><br/>
                                      <DatePicker
                                        locale="it"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        selected={(values.start && new Date(values.start)) || null}
                                        onChange={val => setFieldValue('start', val ? new Date(val).toISOString() : null)}/>
                                    </Form.Group>
                                  </Col>
                                  <Col>
                                    <Form.Group>
                                      <Form.Label>{t('END_DATE')}</Form.Label><br/>
                                      <DatePicker
                                        locale="it"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        selected={(values.end && new Date(values.end)) || null}
                                        onChange={val => setFieldValue('end', val ? new Date(val).toISOString() : null)}/>
                                    </Form.Group>
                                  </Col>
                                </Form.Row>
                                <Button type="submit" className="profile-auth-action profile-auth-action--excel" style={{marginTop: 50, marginBottom: 50}}><FaFileExcel aria-hidden="true"/>{t('EXTRACT_XLS')}</Button>
                              </Form>
                            </Row>
                          </Container>
                        </Fragment>
                      );
                    }}
                  </Formik>
                  </div>
                </Tab>
              </Tabs>
            </Col>
          </Row>
          <PlayerNewSubscription/>
          <PlayerInsufficientFunds/>
        </Container>
      </Fade>
      <Dialog
        ref={el => {
          dialog = el;
        }}/>
      <Modal show={isStorageModalVisible} onHide={hideStorageModal} dialogClassName="player-storage-skywork-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t('MY_VIDEO_MANAGE_SPACE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Fragment>
            <div className="player-storage-skywork-intro">
              <span><FaDatabase aria-hidden="true"/></span>
              <div><strong>{t('MY_VIDEO_SPACE')}</strong><p>{t('STORAGE_PLANS_DESCRIPTION')}</p></div>
            </div>
            <div className="player-storage-skywork-current">
              <span>{t('CURRENT_SPACE')}</span>
              <strong>{(Math.floor(profile.videoQuota)/(1000000000)).toFixed(2)}GB <small>{t('OF_SPACE', {value: storageLimit[profile.storageType]})}</small></strong>
            </div>
            {profile.storageType === "xs" && profile.storageLegacyFree ? <p className="player-storage-skywork-notice">{t('MY_VIDEO_LEGACY_FREE_NOTICE')}</p> : null}
            <p className="player-storage-skywork-prompt">{t('MY_VIDEO_CHOOSE_SPACE')}</p>
            <Formik
              initialValues={{storageType: selectableStorageType}}
              onSubmit={changeStorageType}
            >
              {({
                handleSubmit,
                setFieldValue,
                values
              }) => {
                return (
                  <Form className="player-storage-skywork-form" onSubmit={handleSubmit}>
                    <Form.Group className="player-storage-skywork-options">
                      <Form.Check className="player-storage-skywork-option" required name="storageType" type="radio" label={t('STORAGE_PRICE_S',{price: storagePrices.s})} checked={values.storageType === "s"} onChange={() => setFieldValue('storageType', "s")}/>
                      <Form.Check className="player-storage-skywork-option" required name="storageType" type="radio" label={t('STORAGE_PRICE_M',{price: storagePrices.m})} checked={values.storageType === "m"} onChange={() => setFieldValue('storageType', "m")}/>
                      <Form.Check className="player-storage-skywork-option" required name="storageType" type="radio" label={t('STORAGE_PRICE_L',{price: storagePrices.l})} checked={values.storageType === "l"} onChange={() => setFieldValue('storageType', "l")}/>
                      <Form.Check className="player-storage-skywork-option" required name="storageType" type="radio" label={t('STORAGE_PRICE_XL',{price: storagePrices.xl})} checked={values.storageType === "xl"} onChange={() => setFieldValue('storageType', "xl")}/>
                      <Form.Check className="player-storage-skywork-option" required name="storageType" type="radio" label={t('STORAGE_PRICE_XXL',{price: storagePrices.xxl})} checked={values.storageType === "xxl"} onChange={() => setFieldValue('storageType', "xxl")}/>
                      <Form.Check className="player-storage-skywork-option" required name="storageType" type="radio" label={t('STORAGE_PRICE_XXXL',{price: storagePrices.xxxl})} checked={values.storageType === "xxxl"} onChange={() => setFieldValue('storageType', "xxxl")}/>
                    </Form.Group>
                    <Button type="submit" className="profile-auth-action profile-auth-action--save"><FaSave aria-hidden="true"/>{t('SAVE')}</Button>
                  </Form>
                );
              }}
            </Formik>
          </Fragment>
        </Modal.Body>
        <Modal.Footer>
          <Button className="profile-auth-action profile-auth-action--close" onClick={hideStorageModal}>
            <FaTimes aria-hidden="true"/>{t('CLOSE_BUTTON')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

PlayerProfile.propTypes = {
  profile: PropTypes.object,
  save: PropTypes.func.isRequired,
  deleteProfile: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  token: PropTypes.string,
  removeFile: PropTypes.func.isRequired,
  partners: PropTypes.array,
  subscriptions: PropTypes.array,
  selectedPlayerGroups: PropTypes.array,
  changeAutorenew: PropTypes.func.isRequired,
  archiveSubscription: PropTypes.func.isRequired,
  showReplayerTV: PropTypes.func.isRequired,
  createBookingReport: PropTypes.func.isRequired,
  createSubscriptionReport: PropTypes.func.isRequired,
  storagePrices: PropTypes.object,
  changeStorageType: PropTypes.func.isRequired,
  isStorageModalVisible: PropTypes.bool.isRequired,
  showStorageModal: PropTypes.func.isRequired,
  hideStorageModal: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

PlayerProfile.defaultProps = {
  profile: null,
  token: null,
  partners: [],
  subscriptions: [],
  selectedPlayerGroups: [],
  storagePrices: {},
  isStorageModalVisible: false,
};

const mapStateToProps = ({player: {isStorageModalVisible}}) => ({
  isStorageModalVisible,
});

const mapDispatchToProps = dispatch => ({
  changeAutorenew: (subscription) => () => {
    subscription.autorenew = !subscription.autorenew
    dispatch(changeSubscription(subscription));
  },
  archiveSubscription: (subscription) => {
    subscription.archived = !subscription.archived
    dispatch(changeSubscription(subscription));
  },
  createBookingReport: values => {
    dispatch(reportBookings(values));
  },
  createSubscriptionReport: values => {
    dispatch(reportSubscriptions(values));
  },
  createTransactionReport: values => {
    dispatch(reportTransactions(values));
  },
  changeStorageType: newStorageType => {
    dispatch(changeStorageType(newStorageType));
    dispatch(hideStorageModal());
  },
  showStorageModal: () => {
    dispatch(showStorageModal());
  },
  hideStorageModal: () => {
    dispatch(hideStorageModal());
  },
  showInvalidAmountMessage: () => {
    dispatch(showMessage('ACCOUNT_TOPUP', 'MIN_MAX',{min: '5,00 €', max: '9999,00 €'}));
  },
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(PlayerProfile));
